import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const adminApi = axios.create({
  baseURL: `${BASE}/api/admin`,
  headers: { 'Content-Type': 'application/json' }
});

// Attach token on every request
adminApi.interceptors.request.use(config => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, clear token and redirect to admin login
adminApi.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const adminLogin  = (email, password) =>
  adminApi.post('/auth/login', { email, password });

export const getAdminMe  = () => adminApi.get('/auth/me');

export const updateAdminTheme = (mode, accent) =>
  adminApi.put('/auth/theme', { mode, accent });

// ── Dashboard ─────────────────────────────────────────────────
export const getDashboardStats = () => adminApi.get('/dashboard/stats');

// ── Users ─────────────────────────────────────────────────────
export const getUsers = (params = {}) =>
  adminApi.get('/users', { params });

export const getUser = id =>
  adminApi.get(`/users/${id}`);

export const createUser = data =>
  adminApi.post('/users', data);

export const updateUser = (id, data) =>
  adminApi.put(`/users/${id}`, data);

export const updateUserPublicProfile = (id, publicProfile) =>
  adminApi.put(`/users/${id}/public-profile`, { publicProfile });

export const resetUserPassword = (id, newPassword) =>
  adminApi.post(`/users/${id}/reset-password`, { newPassword });

export const deleteUser = id =>
  adminApi.delete(`/users/${id}`);

// ── Providers ─────────────────────────────────────────────────
export const getProviders = (params = {}) =>
  adminApi.get('/providers', { params });

export const getProvider = id =>
  adminApi.get(`/providers/${id}`);

export const createProvider = data =>
  adminApi.post('/providers', data);

export const updateProvider = (id, data) =>
  adminApi.put(`/providers/${id}`, data);

export const verifyProvider = (id, isVerified) =>
  adminApi.put(`/providers/${id}/verify`, { isVerified });

export const disableProvider = (id, disabled, reason = '') =>
  adminApi.put(`/providers/${id}/disable`, { disabled, reason });

export const resetProviderPassword = (id, newPassword) =>
  adminApi.post(`/providers/${id}/reset-password`, { newPassword });

export const deleteProvider = (id, hardDelete = false) =>
  adminApi.delete(`/providers/${id}`, { params: { hardDelete } });

export const getProviderAgents = id =>
  adminApi.get(`/providers/${id}/agents`);

export const getProviderAccessHistory = id =>
  adminApi.get(`/providers/${id}/access-history`);

// ── Assets ────────────────────────────────────────────────────
export const getAssets = (params = {}) =>
  adminApi.get('/assets', { params });

export const getAsset = assetId =>
  adminApi.get(`/assets/${assetId}`);

export const generateBatch = data =>
  adminApi.post('/assets/generate-batch', data);

export const invalidateAsset = (assetId, reason) =>
  adminApi.put(`/assets/${assetId}/invalidate`, { reason });

export const reactivateAsset = assetId =>
  adminApi.put(`/assets/${assetId}/reactivate`);

// ── Batch downloads ───────────────────────────────────────────
export const getBatch     = batchNumber => adminApi.get(`/assets/batch/${batchNumber}`);
export const getBatchList = ()           => adminApi.get('/assets/batches/list');

// These return non-JSON — use the base URL directly
export const batchPrintUrl = batchNumber => `${BASE}/api/admin/assets/batch/${encodeURIComponent(batchNumber)}/print`;
export const batchCsvUrl   = batchNumber => `${BASE}/api/admin/assets/batch/${encodeURIComponent(batchNumber)}/csv`;
export const batchZipUrl   = batchNumber => `${BASE}/api/admin/assets/batch/${encodeURIComponent(batchNumber)}/zip`;

// ── Inventory ─────────────────────────────────────────────────
export const getInventory = (params = {}) =>
  adminApi.get('/assets/inventory/items', { params });

export const getInventoryStats = () =>
  adminApi.get('/assets/inventory/stats/summary');

export const updateInventoryItem = (assetId, data) =>
  adminApi.put(`/assets/inventory/${assetId}`, data);

export const shipInventoryItem = (assetId, data) =>
  adminApi.put(`/assets/inventory/${assetId}/ship`, data);

export const addInventoryStock = data =>
  adminApi.post('/assets/inventory/add', data);

export const getInventoryItemById = inventoryId =>
  adminApi.get(`/assets/inventory/item/${inventoryId}`);

export const updateInventoryItemById = (inventoryId, data) =>
  adminApi.put(`/assets/inventory/item/${inventoryId}`, data);

export const deleteInventoryItemById = inventoryId =>
  adminApi.delete(`/assets/inventory/item/${inventoryId}`);

export const linkQrToInventory = (inventoryId, assetId) =>
  adminApi.post(`/assets/inventory/${inventoryId}/link-qr`, { assetId });

export const getInventoryBatches = () =>
  adminApi.get('/assets/inventory/batches');

// ── Site Settings ─────────────────────────────────────────────
export const getSiteSettings       = ()   => adminApi.get('/settings');
export const updateUserTabs        = tabs => adminApi.put('/settings/user-tabs', { userTabs: tabs });
export const updateProviderTabs    = tabs => adminApi.put('/settings/provider-tabs', { providerTabs: tabs });
export const updatePublicSections  = secs => adminApi.put('/settings/public-sections', { publicProfileSections: secs });
export const getPublicSettings     = ()   => adminApi.get('/settings/public');

// ── Admin management ──────────────────────────────────────────
export const getAdmins             = ()              => adminApi.get('/admins');
export const getAdmin              = id             => adminApi.get(`/admins/${id}`);
export const createAdmin           = data           => adminApi.post('/create', data);
export const updateAdmin           = (id, data)     => adminApi.put(`/admins/${id}`, data);
export const updateAdminPermissions= (id, perms)    => adminApi.put(`/admins/${id}/permissions`, { permissions: perms });
export const toggleAdminActive     = id             => adminApi.put(`/admins/${id}/toggle-active`);
export const resetAdminPassword    = (id, password) => adminApi.post(`/admins/${id}/reset-password`, { newPassword: password });
export const deleteAdmin           = id             => adminApi.delete(`/admins/${id}`);
export default adminApi;
